import {
  BayLevelEnum,
  ForeAftEnum,
  IBayLevelData,
  IBulkheadInfo,
  IMasterCGs,
  ISizeSummary,
  TContainerLengths,
} from "open-vessel-definition";
import { CellInfo, ICellCGsInfo } from "./CellInfo";
import { EIGHTANDHALF_FEET_IN_MM, NINE_SIX_FEET_IN_MM } from "./consts";
import { IBayPattern, IRowPattern, ISlotPattern, ITierPattern } from "../types/IPositionPatterns";
import { pad2, sortNumericAsc } from "@tedivo/tedivo-pure-helpers";

import { SmartSlotsData } from "../slotsData/SmartSlotsData";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { getContainersCgs } from "./containerCgs/getContainersCgs";

export class BaySizesAndCgs {
  isoBay: IBayPattern;
  level: BayLevelEnum;
  bayLabel: string | undefined;
  pairedBay: ForeAftEnum | undefined;
  bulkhead: IBulkheadInfo | undefined;

  minTier = Infinity;
  maxTier = -Infinity;
  maxRow = 0;
  centerLineRow: 0 | 1 = 0;

  cellsInfo: Map<ISlotPattern, CellInfo> = new Map();
  perRowAverageBayTcg: Map<IRowPattern, number>;
  perRowAddedUpTcgDeviations: Map<IRowPattern, number>;

  slotsKeys: ISlotPattern[];

  sizes: Array<TContainerLengths>;

  missingImportantLcgs = false;
  missingImportantVcgs = false;
  missingImportantTcgs = false;

  countLcgs = 0;
  definedLcgs = 0;
  countTcgs = 0;
  definedTcgs = 0;
  countBottomBase = 0;
  definedBottomBase = 0;

  pairedToBay: IBayPattern | undefined = undefined;
  someTierInPairedBayHas40 = false;

  readonly definedSlots: number;

  maxBottomBase = -Infinity;
  minBottomBase = Infinity;
  minLcg = Infinity;
  maxLcg = -Infinity;
  minTcg = Infinity;
  maxTcg = -Infinity;
  minSize = Infinity as TContainerLengths;
  maxSize = 0 as TContainerLengths;

  /** Max height of the deck in mm when it has been defined. Use {@link getDeckMaxTheoreticalHeight} to get the theoretical height. */
  maxHeight: number | undefined;

  constructor(private bl: IBayLevelData, sizeSummary: ISizeSummary, masterCGs: IMasterCGs) {
    this.isoBay = bl.isoBay;
    this.level = bl.level;
    this.bayLabel = bl.label20 || bl.label40;
    this.pairedBay = bl.pairedBay;
    this.bulkhead = bl.bulkhead;
    this.maxHeight = Math.max(
      bl.perRowInfo?.common?.maxHeight ?? 0,
      Math.max(...Object.values(bl.perRowInfo?.each || {}).map((r) => r.maxHeight ?? 0)),
    );

    this.sizes = [];

    const { infoByContLength, perSlotInfo, perRowInfo } = bl;
    const commonRowInfo = perRowInfo?.common;
    const perRowInfoEach = perRowInfo?.each;

    const smartSlotsData = new SmartSlotsData(Object.values(perSlotInfo || {}));

    const rowTcgsAll: Record<IRowPattern, number[]> = {};

    const { sizes, definedSlots } = smartSlotsData.getAllSizesInSet();

    const {
      rows: rowsFromSlots,
      tiersByRow: tiersByRowFromSlots,
      minTier,
      maxTier,
    } = smartSlotsData.getRowsAndTiers();

    this.slotsKeys = smartSlotsData.getSlotsKeys();
    this.sizes = sizes || [];

    this.definedSlots = definedSlots;

    this.minTier = Number(minTier);
    this.maxTier = Number(maxTier);
    this.maxRow = Math.max(...rowsFromSlots.map((r) => Number(r)));
    this.centerLineRow = bl.centerLineRow ?? sizeSummary.centerLineRow ?? 0;

    const cellsInfo = smartSlotsData.getSmartSlotsData().reduce((acc, sSlotData) => {
      const pos = sSlotData.pos;
      const row = sSlotData.row;
      const tier = sSlotData.tier;

      if (sSlotData.data.restricted) {
        acc.set(pos, new CellInfo({ row, tier, restricted: 1 }));
        return acc;
      }

      const isAbove = bl.level === BayLevelEnum.ABOVE;

      const slotSizes = sSlotData.allSizes;

      const currentRowInfo = perRowInfoEach?.[row];

      const bottomIsoTier = tiersByRowFromSlots[row]?.minTier
        ? pad2(tiersByRowFromSlots[row].minTier)
        : undefined;

      // Bottom base is the one defined in the row, or in common, or in masterCGs
      const bottomBase =
        currentRowInfo?.bottomBase ??
        commonRowInfo?.bottomBase ??
        (bottomIsoTier !== undefined ? masterCGs.bottomBases[bottomIsoTier] : undefined);

      const tcg =
        currentRowInfo?.tcg ?? (isAbove ? masterCGs.aboveTcgs[row] : masterCGs.belowTcgs[row]);

      const infoBySize = slotSizes.reduce((acc, size) => {
        const obj: ICellCGsInfo = {
          size,
          lcg: currentRowInfo?.rowInfoByLength?.[size]?.lcg ?? infoByContLength?.[size]?.lcg,
          tcg,
          bottomBase,
          bottomTier: bottomIsoTier,
          extending: this.pairedBay,
          isoOddBay: this.isoBay,
          bayLevel: this.level,
        };

        if (!rowTcgsAll[row]) rowTcgsAll[row] = [tcg];
        else rowTcgsAll[row].push(tcg);

        this.countLcgs += 1;
        if (obj.lcg !== undefined) {
          if (this.minLcg > obj.lcg) this.minLcg = obj.lcg;
          if (this.maxLcg < obj.lcg) this.maxLcg = obj.lcg;
          this.definedLcgs += 1;
        }

        this.countTcgs += 1;
        if (obj.tcg !== undefined) {
          if (this.minTcg > obj.tcg) this.minTcg = obj.tcg;
          if (this.maxTcg < obj.tcg) this.maxTcg = obj.tcg;
          this.definedTcgs += 1;
        }

        this.countBottomBase += 1;
        if (obj.bottomBase !== undefined) {
          this.definedBottomBase += 1;
          if (this.minBottomBase > obj.bottomBase) this.minBottomBase = obj.bottomBase;
          if (this.maxBottomBase < obj.bottomBase) this.maxBottomBase = obj.bottomBase;
        }

        if (size > this.maxSize) this.maxSize = size;
        if (size < this.minSize) this.minSize = size;

        acc[`${size}`] = obj;

        return acc;
      }, {} as Record<`${TContainerLengths}`, ICellCGsInfo>);

      const result = new CellInfo({
        row,
        tier,
        pairedBay: this.pairedBay,
        maxSize: sSlotData.maxSize,
        sizes: infoBySize,
        isReefer: sSlotData.data.reefer,
      });

      acc.set(pos, result);

      return acc;
    }, new Map<ISlotPattern, CellInfo>());

    this.missingImportantLcgs = this.definedLcgs < this.countLcgs;
    this.missingImportantTcgs = this.definedTcgs < this.countTcgs;
    this.missingImportantVcgs = this.definedBottomBase < this.countBottomBase;

    this.cellsInfo = cellsInfo;

    const rowsTcgs = new Map();
    const perRowAddedUpTcgDeviations = new Map();

    (Object.keys(rowTcgsAll) as IRowPattern[]).forEach((row) => {
      const filteredRow = rowTcgsAll[row].filter((v) => !isNaN(v));

      const tcgsSum = filteredRow.reduce((acc, v) => acc + v, 0);
      const tcgsCount = filteredRow.length;

      if (tcgsCount) {
        const avgTcg = tcgsSum / tcgsCount;

        rowsTcgs.set(row, avgTcg);

        perRowAddedUpTcgDeviations.set(row,
          filteredRow.reduce((acc, v) => acc + Math.abs(avgTcg - v), 0)
        );
      }
    });

    this.perRowAverageBayTcg = rowsTcgs;
    this.perRowAddedUpTcgDeviations = perRowAddedUpTcgDeviations;

    // console.log("maxHeight",
    //   this.isoBay,
    //   BayLevelEnum[this.level],
    //   this.maxTier, this.minTier,
    //   Math.floor((this.maxTier - this.minTier) * 0.5) + 1,
    //   this.getDeckMaxTheoreticalHeight(true),
    //   this.maxHeight,
    // );
  }

  /** Gets the cell info for a given position
   * @warning This method should be used only internally. Use the function {@link getContainersCgs} passing a whole set of containers instead.
   *  Because it will return the cell info for a given position, but it will not include missing CGs.
   */
  getCellInfo(pos: ISlotPattern): CellInfo | undefined {
    return this.cellsInfo.get(pos);
  }

  /** Gets all the tiers in the deck, ordered ascending */
  getAllTiers(): ITierPattern[] {
    const tiers: ITierPattern[] = [];

    this.cellsInfo.forEach((s) => {
      tiers.push(s.tier);
    });

    return tiers.filter((v, i, a) => a.indexOf(v) === i).sort(sortNumericAsc);
  }

  /** Gets the number of tiers in the deck */
  getNumberOfTiers(): number {
    if (this.maxTier === -Infinity || this.minTier === Infinity) return 0;
    return Math.floor((this.maxTier - this.minTier) * 0.5) + 1;
  }

  /** Gets the max size in the tiers */
  getMaxSizeInTiers(whichTiers?: ITierPattern[]): TContainerLengths {
    const maxSizeInTiers: TContainerLengths[] = [];

    this.cellsInfo.forEach((s) => {
      if ((!whichTiers || whichTiers.includes(s.tier)) && s.maxSize) maxSizeInTiers.push(s.maxSize);
    });

    return Math.max(...maxSizeInTiers) as TContainerLengths;
  }

  /** Gets the minimum bottom base of the current deck */
  getDeckBottomBase(): number | undefined {
    return this.minBottomBase === Infinity ? undefined : this.minBottomBase;
  }

  /** Returns theoretical height (8.6ft or 9.6ft containers) adding up the minBottomBase (via {@link getDeckBottomBase}) */
  getDeckMaxTheoreticalAbsoluteHeight(use9_6ft = false): number | undefined {
    const minBottomBase = this.getDeckBottomBase();

    if (!minBottomBase) return undefined;

    return (this.getDeckMaxTheoreticalHeight(use9_6ft) ?? 0) + minBottomBase;
  }

  /** Returns theoretical height (8.6ft or 9.6ft containers) adding up the minBottomBase */
  getDeckMaxTheoreticalHeight(use9_6ft = false): number {
    return this.getNumberOfTiers() * (use9_6ft ? NINE_SIX_FEET_IN_MM : EIGHTANDHALF_FEET_IN_MM);
  }

  getAvgCGs = (row?: IRowPattern): IGetAvgCgsOutput | undefined => {
    if (!this.sizes || this.sizes.length === 0) return undefined;

    const lcgsBy2040Size = new Map<20 | 40, number>();
    let countLcgLessThan40 = 0;
    let countLcgGreaterOrEqualThan40 = 0;
    let tcgAnySize = 0;
    let countTcgAnySize = 0;
    let lowestBottomBaseAnySize = Infinity;

    const avgCellInfo = this.sizes.reduce((acc, size) => {
      let lcg = 0;
      let tcg = 0;
      let bottomBase = 0;
      let countLcg = 0;
      let countTcg = 0;
      let countBottomBase = 0;

      this.slotsKeys
        .filter((pos) => !row || pos.substring(0, 2) === row)
        .forEach((pos) => {
          const posInfo = this.cellsInfo.get(pos);

          const slotLcg = posInfo?.getLcg(size);
          const slotTcg = posInfo?.getTcg(size);
          const slotBottomBase = posInfo?.getBottomBase(size);

          if (slotLcg !== undefined) {
            lcg += slotLcg;
            countLcg += 1;
            if (size < 40) {
              lcgsBy2040Size.set(20, (lcgsBy2040Size.get(20) ?? 0) + slotLcg);
              countLcgLessThan40 += 1;
            } else {
              lcgsBy2040Size.set(40, (lcgsBy2040Size.get(40) ?? 0) + slotLcg);
              countLcgGreaterOrEqualThan40 += 1;
            }
          }

          if (slotTcg !== undefined) {
            tcg += slotTcg;
            countTcg += 1;
            tcgAnySize += slotTcg;
            countTcgAnySize += 1;
          }

          if (slotBottomBase !== undefined) {
            bottomBase += slotBottomBase;
            countBottomBase += 1;

            if (slotBottomBase < lowestBottomBaseAnySize) lowestBottomBaseAnySize = slotBottomBase;
          }
        });

      if (countLcg + countTcg + countBottomBase === 0) return acc;

      acc.set(size, {
        size,
        lcg: lcg / (countLcg || 1),
        tcg: tcg / (countTcg || 1),
        bottomBase: bottomBase / (countBottomBase || 1),
        bottomTier: this.minTier !== undefined ? pad2(this.minTier) : undefined,
        isoOddBay: this.isoBay,
        bayLevel: this.level,
      });

      return acc;
    }, new Map<TContainerLengths, ICellCGsInfo>());

    return {
      perSize: avgCellInfo,
      isoOddBay: this.isoBay,
      lowestBottomBase: lowestBottomBaseAnySize,
      lowestBottomTier: this.minTier !== undefined ? pad2(this.minTier) : undefined,
      avgTcg: tcgAnySize / (countTcgAnySize || 1),
      avgLcg20: (lcgsBy2040Size.get(20) ?? 0) / (countLcgLessThan40 || 1),
      avgLcg40: (lcgsBy2040Size.get(40) ?? 0) / (countLcgGreaterOrEqualThan40 || 1),
    };
  };

  public isMissingPairingButPlausible(): boolean {
    return this.pairedBay === undefined && this.sizes.some((s) => s >= 40);
  }

  get bayData() {
    return this.bl;
  }
}

export type IGetAvgCgsOutput = {
  perSize: Map<Partial<TContainerLengths>, ICellCGsInfo>;
  avgLcg20: number;
  avgLcg40: number;
  avgTcg: number;
  lowestBottomBase: number;
  lowestBottomTier: ITierPattern | undefined;
  isoOddBay: IBayPattern;
};
